/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {
  createSchedule,
  deleteSchedule as destroySchedule,
  deleteSchedules as destroySchedules,
  loadSchedules,
  updateSchedule,
  updateSchedules
} from '@ion/core/src/services/Connect';
import {
  Entity,
  Schedule,
  Schedules
} from '@ion/core/src/types/entities';
import {LoadSchedulesParams} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageInitializeAction} from '@/types/redux';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../components/prompts/constants';
import {getAccountId} from '../helpers';
import {toggleUserPrompt} from './user-prompt';

// name of the reducer
export const NAME                             = 'adminSchedules';

// initialize schedules ...
export const INITIALIZE_ADMIN_SCHEDULES       = 'INITIALIZE_ADMIN_SCHEDULES';
export const RE_INITIALIZE_ADMIN_SCHEDULES    = 'RE_INITIALIZE_ADMIN_SCHEDULES';

// grid column state
export const SET_GRID_COLUMNS                 = 'SET_ADMIN_SCHEDULES_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS              = 'UPDATE_ADMIN_SCHEDULES_GRID_COLUMNS';

export const ADMIN_SCHEDULES_GRID_REFRESHED   = 'ADMIN_SCHEDULES_GRID_REFRESHED';

// schedules retrieval ...
export const GET_ADMIN_SCHEDULES_REQUEST      = 'GET_ADMIN_SCHEDULES_REQUEST';
export const GET_ADMIN_SCHEDULES_RESPONSE     = 'GET_ADMIN_SCHEDULES_RESPONSE';
export const GET_ADMIN_SCHEDULES_ERROR        = 'GET_ADMIN_SCHEDULES_ERROR';

// schedule add ...
export const ADD_ADMIN_SCHEDULE               = 'ADD_ADMIN_SCHEDULE';
export const ADD_ADMIN_SCHEDULE_REQUEST       = 'ADD_ADMIN_SCHEDULE_REQUEST';
export const ADD_ADMIN_SCHEDULE_RESPONSE      = 'ADD_ADMIN_SCHEDULE_RESPONSE';
export const ADD_ADMIN_SCHEDULE_ERROR         = 'ADD_ADMIN_SCHEDULE_ERROR';

// schedule edit ...
export const EDIT_ADMIN_SCHEDULE              = 'EDIT_ADMIN_SCHEDULE';
export const EDIT_ADMIN_SCHEDULE_REQUEST      = 'EDIT_ADMIN_SCHEDULE_REQUEST';
export const EDIT_ADMIN_SCHEDULE_RESPONSE     = 'EDIT_ADMIN_SCHEDULE_RESPONSE';
export const EDIT_ADMIN_SCHEDULE_ERROR        = 'EDIT_ADMIN_SCHEDULE_ERROR';

// manage schedule associations ...
export const MANAGE_ADMIN_SCHEDULE             = 'MANAGE_ADMIN_SCHEDULE';
export const MANAGE_ADMIN_SCHEDULE_REQUEST     = 'MANAGE_ADMIN_SCHEDULE_REQUEST';
export const MANAGE_ADMIN_SCHEDULE_RESPONSE    = 'MANAGE_ADMIN_SCHEDULE_RESPONSE';
export const MANAGE_ADMIN_SCHEDULE_ERROR       = 'MANAGE_ADMIN_SCHEDULE_ERROR';

// view schedule audit log ...
export const AUDIT_ADMIN_SCHEDULE              = 'AUDIT_ADMIN_SCHEDULE';
export const AUDIT_ADMIN_SCHEDULE_REQUEST      = 'AUDIT_ADMIN_SCHEDULE_REQUEST';
export const AUDIT_ADMIN_SCHEDULE_RESPONSE     = 'AUDIT_ADMIN_SCHEDULE_RESPONSE';
export const AUDIT_ADMIN_SCHEDULE_ERROR        = 'AUDIT_ADMIN_SCHEDULE_ERROR';

// schedule delete ...
export const DELETE_ADMIN_SCHEDULE             = 'DELETE_ADMIN_SCHEDULE';
export const DELETE_ADMIN_SCHEDULE_REQUEST     = 'DELETE_ADMIN_SCHEDULE_REQUEST';
export const DELETE_ADMIN_SCHEDULE_RESPONSE    = 'DELETE_ADMIN_SCHEDULE_RESPONSE';
export const DELETE_ADMIN_SCHEDULE_ERROR       = 'DELETE_ADMIN_SCHEDULE_ERROR';

// bulk schedule edits ...
export const EDIT_ADMIN_SCHEDULES              = 'EDIT_ADMIN_SCHEDULES';
export const EDIT_ADMIN_SCHEDULES_REQUEST      = 'EDIT_ADMIN_SCHEDULES_REQUEST';
export const EDIT_ADMIN_SCHEDULES_RESPONSE     = 'EDIT_ADMIN_SCHEDULES_RESPONSE';
export const EDIT_ADMIN_SCHEDULES_ERROR        = 'EDIT_ADMIN_SCHEDULES_ERROR';

// bulk schedule deletes ...
export const DELETE_ADMIN_SCHEDULES            = 'DELETE_ADMIN_SCHEDULES';
export const DELETE_ADMIN_SCHEDULES_REQUEST    = 'DELETE_ADMIN_SCHEDULES_REQUEST';
export const DELETE_ADMIN_SCHEDULES_RESPONSE   = 'DELETE_ADMIN_SCHEDULES_RESPONSE';
export const DELETE_ADMIN_SCHEDULES_ERROR      = 'DELETE_ADMIN_SCHEDULES_ERROR';

// admin schedules filter model ...
export const SET_ADMIN_SCHEDULES_GRID_FILTER_MODEL = 'SET_ADMIN_SCHEDULES_GRID_FILTER_MODEL';

// common schedule actions...
export const CLEAR_ADMIN_SCHEDULES_ERRORS = 'CLEAR_ADMIN_SCHEDULES_ERRORS';

/**
 * @initializeAdminSchedules
 */
export const initializeAdminSchedules = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_SCHEDULES};
};

export const clearAdminSchedulesErrors = () => ({
  type : CLEAR_ADMIN_SCHEDULES_ERRORS
});

export const reInitializeAdminSchedules = (): PageInitializeAction => (
  {type: RE_INITIALIZE_ADMIN_SCHEDULES}
);

/**
 * @adminSchedulesGridRefreshed
 */
export const adminSchedulesGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_SCHEDULES_GRID_REFRESHED};
};

/**
 * @getAdminSchedules
 */
export const getAdminSchedules = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_SCHEDULES_REQUEST});
    const data: LoadSchedulesParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize,
      includeSubGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    try {
      const {schedules: items, totalCount} = await loadSchedules(data);
      dispatch({type: GET_ADMIN_SCHEDULES_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_SCHEDULES_ERROR, error: err});
      return err;
    }
  };
};

/**
 * @addAdminSchedule
 *
 * Invoked when the Add Admin Schedule editor is to be displayed or removed.
 */
export const addAdminSchedule = (show = true) => ({
  type : ADD_ADMIN_SCHEDULE, show
});

/**
 * @addSchedule
 *
 * Invoked when a new schedule is to be created, delegating to the createSchedule
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const addSchedule = (schedule: Schedule) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_ADMIN_SCHEDULE_REQUEST});
    try {
      const created = await createSchedule(schedule);
      dispatch({type: ADD_ADMIN_SCHEDULE_RESPONSE, schedule: created});
      dispatch(toggleUserPrompt(successAddOptions(Entity.schedule)));
    } catch (err) {
      dispatch({type: ADD_ADMIN_SCHEDULE_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.schedule, err)));
      error(err);
    }
  };
};

/**
 * @editAdminSchedule
 *
 * Invoked when the Edit Admin Schedule editor is to be displayed or removed.
 */
export const editAdminSchedule = (schedule: Schedule, show = true) => ({
  type : EDIT_ADMIN_SCHEDULE, schedule, show
});

/**
 * @editSchedule
 *
 * Invoked when an existing schedule is to be updated, delegating to the updateSchedule
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const editSchedule = (schedule: Schedule) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_SCHEDULE_REQUEST});
    try {
      const updated = await updateSchedule(schedule);
      dispatch({type: EDIT_ADMIN_SCHEDULE_RESPONSE, schedule: updated});
      dispatch(toggleUserPrompt(successEditOptions(Entity.schedule)));
    } catch (err) {
      dispatch({type: EDIT_ADMIN_SCHEDULE_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.schedule, err)));
      error(err);
    }
  };
};

/**
 * @manageAdminSchedule
 *
 * Description TBD
 */
export const manageAdminSchedule = (schedule: Schedule, show = true) => ({
  type : MANAGE_ADMIN_SCHEDULE, schedule, show
});

/**
 * @manageSchedule
 *
 * Description TBD
 */
export const manageSchedule = (schedule: Schedule) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: MANAGE_ADMIN_SCHEDULE_REQUEST});
    try {
      // [ION_SUITE TODO]: const managed = await xxxSchedule(schedule);
      // dispatch({type: MANAGE_ADMIN_SCHEDULE_RESPONSE, schedule: managed});
    } catch (err) {
      dispatch({type: MANAGE_ADMIN_SCHEDULE_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @auditAdminSchedule
 *
 * Invoked when the the schedule auditor is to be displayed or removed.
 */
export const auditAdminSchedule = (schedule: Schedule, show = true) => ({
  type : AUDIT_ADMIN_SCHEDULE, schedule, show
});

/**
 * @auditSchedule
 *
 * Invoked when a schedule audit is to be made, delegating to the scheduleAudit CTC
 * API and dispatching a successful response or error action accordingly.
 */
export const auditSchedule = (schedule: Schedule) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: AUDIT_ADMIN_SCHEDULE_REQUEST});
    try {
      // [ION_SUITE TODO]: const audited = await xxxSchedule(schedule);
      // dispatch({type: AUDIT_ADMIN_SCHEDULE_RESPONSE, schedule: audited});
    } catch (err) {
      dispatch({type: AUDIT_ADMIN_SCHEDULE_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @deleteAdminSchedule
 *
 * Invoked when the Delete Admin Schedule Dialog is to be displayed or removed.
 */
export const deleteAdminSchedule = (schedule: Schedule, show = true) => ({
  type : DELETE_ADMIN_SCHEDULE, schedule, show
});

/**
 * @deleteSchedule
 *
 * Invoked when an existing schedule is to be deleted, delegating to the destroySchedule
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteSchedule = (schedule: Schedule) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_SCHEDULE_REQUEST});
    try {
      const deleted = await destroySchedule(schedule);
      dispatch({type: DELETE_ADMIN_SCHEDULE_RESPONSE, schedule: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_SCHEDULE_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @editAdminSchedules
 *
 * Invoked when the Bulk Edit Admin Schedules Dialog is to be displayed or removed.
 */
export const editAdminSchedules = (schedules: Schedules, show = true) => ({
  type : EDIT_ADMIN_SCHEDULES, schedules, show
});

/**
 * @editSchedules
 *
 * Invoked when a collection of existing schedules are to be updated, delegating to
 * the updateSchedules CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const editSchedules = (schedules: Schedules) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_SCHEDULES_REQUEST});
    try {
      const {results, errors} = await updateSchedules(schedules);
      dispatch(toggleUserPrompt(successEditOptions(translate('common:entityLabels.schedules'),
        {results, items: schedules})));
      dispatch({type: EDIT_ADMIN_SCHEDULES_RESPONSE, schedules: results});
    } catch (err) {
      dispatch({type: EDIT_ADMIN_SCHEDULES_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(translate('common:entityLabels.schedules'))));
      error(err);
    }
  };
};

/**
 * @deleteAdminSchedules
 *
 * Invoked when the Bulk Delete Admin Schedules Dialog is to be displayed or removed.
 */
export const deleteAdminSchedules = (schedules: Schedules, show = true) => ({
  type : DELETE_ADMIN_SCHEDULES, schedules, show
});

/**
 * @deleteSchedules
 *
 * Invoked when a collection of existing schedules are to be deleted, delegating to
 * the destroySchedules CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const deleteSchedules = (schedules: Schedules) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_SCHEDULES_REQUEST});
    try {
      const {results, errors} = await destroySchedules(schedules);
      dispatch({type: DELETE_ADMIN_SCHEDULES_RESPONSE, schedules: results});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_SCHEDULES_ERROR, error: err});
      error(err);
    }
  };
};

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_ADMIN_SCHEDULES_GRID_FILTER_MODEL, model
});
